import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { GetRights } from 'nallian-shared-ui/lib/selectors';
import { hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import { history } from 'nallian-shared-ui/lib/config/history';
import { Modal, Loader, Dimmer, Icon, Flex } from 'nallian-shared-ui/lib/components';
import { Table } from 'semantic-ui-react';
import communityAppService from '../../services/communityApps/communityAppService';
import communityAppClientsService from '../../services/communityAppClients/communityAppClientsService';
import removeCommunityAppClientService from '../../services/communityAppClients/removeCommunityAppClientService';
import { COMMUNITY_DETAIL_TABS } from '../../constants';
import AddCommunityAppClient from './AddCommunityAppClient';

const ClientRow = ({ client, isLoading, onRemove, readOnly }) => {
  const handleRemove = useCallback(() => onRemove(client.id), [client, onRemove]);

  return (
    <Table.Row key={client.id}>
      <Table.Cell width="12">{client.clientId}</Table.Cell>
      <Table.Cell width="4" textAlign="right">
        {!readOnly && <Icon iconbutton name="trash" onClick={handleRemove} loading={isLoading} />}
      </Table.Cell>
    </Table.Row>
  );
};

ClientRow.propTypes = {
  client: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const CommunityAppClients = ({
  open,
  dispatchCommunityAppClientsGet,
  communityAppClientsFetch,
  dispatchCommunityAppGet,
  communityAppFetch,
  dispatchRemoveCommunityAppClientDelete,
  removeCommunityAppClientFetch,
}) => {
  const { t } = useTranslation();
  const { id, actionId } = useParams();

  const rights = useSelector(GetRights);

  const readOnly = useMemo(() => !hasRight(rights, 'COMMUNITYAPPS.MANAGE.ALL'), [rights]);

  useEffect(() => {
    if (id && open && actionId) dispatchCommunityAppClientsGet(actionId, t);
  }, [dispatchCommunityAppClientsGet, actionId, id, open, t]);

  useEffect(() => {
    if (open && id && actionId) {
      dispatchCommunityAppGet(actionId, undefined, t);
    }
  }, [id, open, actionId, t, dispatchCommunityAppGet]);

  const handleClose = useCallback(() => {
    history.push(`/communities/edit/${id}/${COMMUNITY_DETAIL_TABS.APPS}`);
  }, [id]);

  const handleRemoveClient = useCallback(
    clientId => dispatchRemoveCommunityAppClientDelete(actionId, clientId, t),
    [dispatchRemoveCommunityAppClientDelete, actionId, t]
  );

  return (
    <Modal open={open} onClose={handleClose} closeIcon>
      <Modal.Header>
        {t('manageClientsForCommunityApp')} {actionId}
      </Modal.Header>
      <Modal.Content>
        {communityAppFetch.pending || communityAppClientsFetch.pending ? (
          <Dimmer active inverted>
            <Loader />
          </Dimmer>
        ) : (
          <>
            <Table basic="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width="12">{t('Clients')}</Table.HeaderCell>
                  <Table.HeaderCell width="4" />
                </Table.Row>
              </Table.Header>
              {communityAppClientsFetch.value?.length > 0 ? (
                <Table.Body>
                  {(communityAppClientsFetch.value || []).map(client => (
                    <ClientRow
                      readOnly={readOnly}
                      key={client.id}
                      client={client}
                      onRemove={handleRemoveClient}
                      isLoading={removeCommunityAppClientFetch?.meta?.id === client.id}
                    />
                  ))}
                </Table.Body>
              ) : (
                <Flex margin="1rem 0 0">
                  <i>{t('noClientsYet')}</i>
                </Flex>
              )}
            </Table>
            {!readOnly && <AddCommunityAppClient communityAppId={actionId} />}
          </>
        )}
      </Modal.Content>
    </Modal>
  );
};

CommunityAppClients.propTypes = {
  dispatchCommunityAppClientsGet: PropTypes.func.isRequired,
  communityAppClientsFetch: PropTypes.object.isRequired,
  dispatchCommunityAppGet: PropTypes.func.isRequired,
  communityAppFetch: PropTypes.object.isRequired,
  dispatchRemoveCommunityAppClientDelete: PropTypes.func.isRequired,
  removeCommunityAppClientFetch: PropTypes.object.isRequired,
  open: PropTypes.bool,
};

CommunityAppClients.defaultProps = { open: false };

export default compose(
  communityAppService,
  communityAppClientsService,
  removeCommunityAppClientService
)(CommunityAppClients);
